import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { Entity } from '@backstage/catalog-model';
import { InfoCard, Link, Progress } from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';
import {
  entityRouteParams,
  entityRouteRef,
  useEntity,
  useRelatedEntities,
} from '@backstage/plugin-catalog-react';

import { makeStyles } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import MuiLink from '@material-ui/core/Link';
import Tooltip from '@material-ui/core/Tooltip';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import AppsIcon from '@material-ui/icons/Apps';
import DnsIcon from '@material-ui/icons/Dns';
import GroupIcon from '@material-ui/icons/Group';
import LayersIcon from '@material-ui/icons/Layers';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';

const DAAS_PROJECT_TYPE = 'daas-project';
const DAAS_APP_TYPES = new Set(['daas-application', 'daas-application-set']);
const CLUSTER_TYPE = 'kubernetes-cluster';
const ARGOCD_URL_ANNOTATION = 'tomtom.com/daas-argocd-url';

const KAAP_COLOR = '#2563eb';
const DAAS_COLOR = '#0d9488';
const OWNER_COLOR = '#7c3aed';

const specType = (entity: Entity): string =>
  ((entity.spec?.type as string | undefined) ?? '').toLowerCase();

const entityTitle = (entity: Entity): string =>
  entity.metadata.title ?? entity.metadata.name;

const useStyles = makeStyles(theme => ({
  legend: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  legendSwatch: { width: 12, height: 12, borderRadius: 3, flexShrink: 0 },
  canvas: {
    position: 'relative',
    width: '100%',
    overflowX: 'auto',
  },
  edges: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    zIndex: 0,
  },
  columns: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(3),
    minWidth: 460,
    padding: theme.spacing(1, 0),
  },
  lane: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    flex: 1,
    minWidth: 130,
  },
  laneCenter: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    flex: '0 0 auto',
  },
  laneRight: { alignItems: 'flex-end' },
  nodeWrapper: { display: 'flex', maxWidth: '100%' },
  laneLabel: {
    fontSize: '0.65rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: theme.palette.text.disabled,
    marginBottom: theme.spacing(0.5),
  },
  node: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.25),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    borderLeftWidth: 4,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
    textDecoration: 'none',
    color: theme.palette.text.primary,
    transition: 'box-shadow 120ms ease, transform 120ms ease',
    maxWidth: 190,
    '&:hover': {
      boxShadow: theme.shadows[4],
      transform: 'translateY(-1px)',
    },
  },
  nodeHub: {
    padding: theme.spacing(1.5, 1.75),
    borderWidth: 2,
    borderLeftWidth: 2,
    boxShadow: theme.shadows[3],
  },
  nodeIcon: { fontSize: '1.25rem', flexShrink: 0 },
  nodeBody: { minWidth: 0, display: 'flex', flexDirection: 'column' },
  nodeName: {
    fontSize: '0.85rem',
    fontWeight: 600,
    lineHeight: 1.2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    wordBreak: 'break-word',
  },
  nodeType: {
    fontSize: '0.65rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.2,
  },
  argocd: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
    fontSize: '0.7rem',
    marginLeft: 'auto',
    paddingLeft: theme.spacing(0.5),
  },
  emptyNode: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(1.5, 1.25),
    borderRadius: 8,
    border: `1px dashed ${theme.palette.divider}`,
    color: theme.palette.text.disabled,
    fontSize: '0.75rem',
    fontStyle: 'italic',
    textAlign: 'center',
    maxWidth: 300,
  },
}));

type NodeInfo = {
  entity?: Entity;
  key: string;
  title: string;
  typeLabel: string;
  icon: React.ReactNode;
  color: string;
  argocdUrl?: string;
  placeholder?: boolean;
};

const GraphNode = ({
  info,
  hub,
  innerRef,
}: {
  info: NodeInfo;
  hub?: boolean;
  innerRef?: (el: HTMLDivElement | null) => void;
}) => {
  const classes = useStyles();
  const catalogEntityRoute = useRouteRef(entityRouteRef);

  if (info.placeholder) {
    return (
      <div
        ref={innerRef}
        className={classes.nodeWrapper}
        data-color={info.color}
      >
        <div className={classes.emptyNode}>{info.title}</div>
      </div>
    );
  }

  const content = (
    <>
      <span style={{ color: info.color }} className={classes.nodeIcon}>
        {info.icon}
      </span>
      <span className={classes.nodeBody}>
        <span className={classes.nodeName}>{info.title}</span>
        <span className={classes.nodeType}>{info.typeLabel}</span>
      </span>
      {info.argocdUrl && (
        <Tooltip title="Open in ArgoCD" arrow>
          <MuiLink
            href={info.argocdUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.argocd}
            onClick={e => e.stopPropagation()}
          >
            ArgoCD
            <OpenInNewIcon style={{ fontSize: '0.8rem' }} />
          </MuiLink>
        </Tooltip>
      )}
    </>
  );

  const style = { borderLeftColor: info.color } as React.CSSProperties;
  const nodeClass = `${classes.node} ${hub ? classes.nodeHub : ''}`;

  const inner = info.entity ? (
    <Link
      to={catalogEntityRoute(entityRouteParams(info.entity))}
      className={nodeClass}
      style={style}
    >
      {content}
    </Link>
  ) : (
    <div
      className={nodeClass}
      style={hub ? { ...style, borderColor: info.color } : style}
    >
      {content}
    </div>
  );

  return (
    <div ref={innerRef} className={classes.nodeWrapper} data-color={info.color}>
      {inner}
    </div>
  );
};

export const KaaPTenantRelationsCard = () => {
  const classes = useStyles();
  const { entity } = useEntity();

  const { entities: dependsOn, loading: dependsOnLoading } = useRelatedEntities(
    entity,
    { type: 'dependsOn' },
  );
  const { entities: dependents, loading: dependentsLoading } =
    useRelatedEntities(entity, { type: 'dependencyOf' });
  const { entities: ownedBy, loading: ownedByLoading } = useRelatedEntities(
    entity,
    { type: 'ownedBy' },
  );

  const loading = dependsOnLoading || dependentsLoading || ownedByLoading;

  const owners = (ownedBy ?? []).filter(e => e.kind.toLowerCase() === 'group');
  const clusters = (dependsOn ?? []).filter(
    e => e.kind.toLowerCase() === 'resource' && specType(e) === CLUSTER_TYPE,
  );
  const projects = (dependents ?? []).filter(
    e => specType(e) === DAAS_PROJECT_TYPE,
  );
  const applications = (dependents ?? []).filter(e =>
    DAAS_APP_TYPES.has(specType(e)),
  );

  const toNode = (e: Entity, typeLabel: string, color: string): NodeInfo => ({
    entity: e,
    key: `${e.kind}:${e.metadata.namespace ?? 'default'}/${e.metadata.name}`,
    title: entityTitle(e),
    typeLabel,
    color,
    icon: color === KAAP_COLOR ? <DnsIcon /> : undefined,
    argocdUrl: e.metadata.annotations?.[ARGOCD_URL_ANNOTATION],
  });

  const leftNodes: NodeInfo[] = [
    ...owners.map(e => ({
      ...toNode(e, 'Owner (Team)', OWNER_COLOR),
      icon: <GroupIcon />,
    })),
    ...clusters.map(e => ({
      ...toNode(e, 'KaaP Cluster', KAAP_COLOR),
      icon: <DnsIcon />,
    })),
  ];

  const rightNodes: NodeInfo[] = [
    ...projects.map(e => ({
      ...toNode(e, 'DaaS Project', DAAS_COLOR),
      icon: <AccountTreeIcon />,
    })),
    ...applications.map(e => ({
      ...toNode(e, 'DaaS Application', DAAS_COLOR),
      icon: <AppsIcon />,
    })),
  ];

  if (leftNodes.length === 0) {
    leftNodes.push({
      key: 'no-cluster',
      title: 'No cluster linked',
      typeLabel: '',
      icon: null,
      color: KAAP_COLOR,
      placeholder: true,
    });
  }
  if (rightNodes.length === 0) {
    rightNodes.push({
      key: 'no-daas',
      title: 'No DaaS deployments yet',
      typeLabel: '',
      icon: null,
      color: DAAS_COLOR,
      placeholder: true,
    });
  }

  const hubNode: NodeInfo = {
    entity,
    key: 'hub',
    title: entityTitle(entity),
    typeLabel: 'KaaP Tenant',
    icon: <LayersIcon />,
    color: KAAP_COLOR,
  };

  const containerRef = useRef<HTMLDivElement | null>(null);
  const hubRef = useRef<HTMLDivElement | null>(null);
  const leftRefs = useRef<Array<HTMLDivElement | null>>([]);
  const rightRefs = useRef<Array<HTMLDivElement | null>>([]);
  leftRefs.current = [];
  rightRefs.current = [];
  const edgesKeyRef = useRef('');
  const [edges, setEdges] = useState<Array<{ d: string; color: string }>>([]);

  const leftCount = leftNodes.length;
  const rightCount = rightNodes.length;

  const recompute = useCallback(() => {
    const container = containerRef.current;
    const hub = hubRef.current;
    if (!container || !hub) return;
    const c = container.getBoundingClientRect();
    const h = hub.getBoundingClientRect();
    const hubLeftX = h.left - c.left;
    const hubRightX = h.right - c.left;
    const hubY = h.top - c.top + h.height / 2;

    const next: Array<{ d: string; color: string }> = [];

    leftRefs.current.forEach(el => {
      if (!el) return;
      const r = el.getBoundingClientRect();
      const x = r.right - c.left;
      const y = r.top - c.top + r.height / 2;
      const mx = (x + hubLeftX) / 2;
      next.push({
        d: `M ${x} ${y} C ${mx} ${y}, ${mx} ${hubY}, ${hubLeftX} ${hubY}`,
        color: el.getAttribute('data-color') ?? KAAP_COLOR,
      });
    });

    rightRefs.current.forEach(el => {
      if (!el) return;
      const r = el.getBoundingClientRect();
      const x = r.left - c.left;
      const y = r.top - c.top + r.height / 2;
      const mx = (x + hubRightX) / 2;
      next.push({
        d: `M ${hubRightX} ${hubY} C ${mx} ${hubY}, ${mx} ${y}, ${x} ${y}`,
        color: el.getAttribute('data-color') ?? DAAS_COLOR,
      });
    });

    const key = next.map(e => `${e.d}|${e.color}`).join(';');
    if (key === edgesKeyRef.current) return;
    edgesKeyRef.current = key;
    setEdges(next);
  }, []);

  useLayoutEffect(() => {
    recompute();
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => recompute());
    ro.observe(container);
    return () => ro.disconnect();
  }, [recompute, loading, leftCount, rightCount]);

  return (
    <InfoCard title="Relations" variant="fullHeight">
      {loading ? (
        <Progress />
      ) : (
        <>
          <Box className={classes.legend}>
            <span className={classes.legendItem}>
              <span
                className={classes.legendSwatch}
                style={{ backgroundColor: KAAP_COLOR }}
              />
              KaaP (tenant &amp; cluster)
            </span>
            <span className={classes.legendItem}>
              <span
                className={classes.legendSwatch}
                style={{ backgroundColor: DAAS_COLOR }}
              />
              DaaS (projects &amp; applications)
            </span>
            <span className={classes.legendItem}>
              <span
                className={classes.legendSwatch}
                style={{ backgroundColor: OWNER_COLOR }}
              />
              Owner (team)
            </span>
          </Box>

          <div className={classes.canvas} ref={containerRef}>
            <svg className={classes.edges}>
              {edges.map((edge, i) => (
                <path
                  key={i}
                  d={edge.d}
                  fill="none"
                  stroke={edge.color}
                  strokeWidth={1.5}
                  strokeOpacity={0.5}
                />
              ))}
            </svg>

            <div className={classes.columns}>
              <div className={classes.lane}>
                <span className={classes.laneLabel}>Owner &amp; cluster</span>
                {leftNodes.map((info, i) => (
                  <GraphNode
                    key={info.key}
                    info={info}
                    innerRef={el => {
                      leftRefs.current[i] = el;
                    }}
                  />
                ))}
              </div>

              <div className={classes.laneCenter}>
                <GraphNode
                  info={hubNode}
                  hub
                  innerRef={el => {
                    hubRef.current = el;
                  }}
                />
              </div>

              <div className={`${classes.lane} ${classes.laneRight}`}>
                <span className={classes.laneLabel}>Deployments</span>
                {rightNodes.map((info, i) => (
                  <GraphNode
                    key={info.key}
                    info={info}
                    innerRef={el => {
                      rightRefs.current[i] = el;
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </InfoCard>
  );
};
